import { useState, useEffect } from 'react'
import { useAuth } from '../context/auth'
import { supabase, Claim, TowJob, Profile, BusinessUser } from '../lib/supabase'
import { createTeamMember } from '../lib/teamService'
import { Modal, TeamMemberSuccessModal, ErrorModal, ConfirmModal } from '../components/Modal'
import {
  Shield, DollarSign, FileText, CheckCircle, AlertTriangle, Settings,
  LogOut, TrendingUp, BarChart3, Activity, Bell, Clock, Plus, Menu, X, User, ChevronDown, BarChart, Users, Trash2
} from 'lucide-react'

type Tab = 'overview' | 'claims' | 'policies' | 'settings' | 'reports' | 'team'

export default function InsurerDashboard() {
  const { profile, signOut } = useAuth()
  const [tab, setTab] = useState<Tab>('overview')
  const [claims, setClaims] = useState<Claim[]>([])
  const [jobs, setJobs] = useState<TowJob[]>([])
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const [showUserMenu, setShowUserMenu] = useState(false)
  
  // Team management state
  const [teamMembers, setTeamMembers] = useState<any[]>([])
  const [showAddTeamMemberModal, setShowAddTeamMemberModal] = useState(false)
  const [newTeamMember, setNewTeamMember] = useState({
    email: '',
    full_name: '',
    role: 'staff' as 'admin' | 'staff'
  })
  const [addingTeamMember, setAddingTeamMember] = useState(false)
  const [showTeamSuccessModal, setShowTeamSuccessModal] = useState(false)
  const [teamSuccessModalData, setTeamSuccessModalData] = useState({
    email: '',
    fullName: '',
    tempPassword: '',
    role: '',
    businessName: '',
    emailSent: false
  })
  const [showTeamErrorModal, setShowTeamErrorModal] = useState(false)
  const [teamErrorModalData, setTeamErrorModalData] = useState({
    title: '',
    message: ''
  })
  const [showConfirmModal, setShowConfirmModal] = useState(false)
  const [confirmModalData, setConfirmModalData] = useState({
    title: '',
    message: '',
    onConfirm: () => {}
  })

  useEffect(() => {
    if (!profile) return
    supabase.from('claims').select('*').order('created_at', { ascending: false }).then(({ data }) => setClaims((data as Claim[]) || []))
    supabase.from('tow_jobs').select('*').order('created_at', { ascending: false }).then(({ data }) => setJobs((data as TowJob[]) || []))
    
    // Load team members
    loadTeamMembers()
  }, [profile])

  const loadTeamMembers = async () => {
    if (!profile) return
    const { data } = await supabase
      .from('business_users')
      .select('*')
      .eq('business_id', profile.id)
    
    // Fetch profiles for each team member
    const userIds = (data || []).map(m => m.user_id)
    const { data: profiles } = await supabase
      .from('profiles')
      .select('id, full_name, email, phone')
      .in('id', userIds)
    
    const profilesMap = (profiles || []).reduce((acc, p) => {
      acc[p.id] = p
      return acc
    }, {} as Record<string, any>)
    
    const teamMembersWithProfiles = (data || []).map(m => ({
      ...m,
      profiles: profilesMap[m.user_id] || null
    }))
    
    setTeamMembers(teamMembersWithProfiles)
  }

  const addTeamMember = async () => {
    if (!profile) return
    setAddingTeamMember(true)

    try {
      // Call server-side API to create team member (prevents session switching)
      const result = await createTeamMember({
        email: newTeamMember.email,
        fullName: newTeamMember.full_name,
        role: newTeamMember.role,
        businessId: profile.id,
        businessName: profile?.insurer_name || profile?.full_name || 'Your Business',
        businessType: 'insurer'
      })

      if (!result.success) {
        setTeamErrorModalData({
          title: 'Team Member Creation Failed',
          message: result.message
        })
        setShowTeamErrorModal(true)
        setAddingTeamMember(false)
        return
      }

      // Refresh team members
      await loadTeamMembers()

      // Reset form and close modal
      setNewTeamMember({ email: '', full_name: '', role: 'staff' })
      setShowAddTeamMemberModal(false)

      // Show success modal
      setTeamSuccessModalData({
        email: newTeamMember.email,
        fullName: newTeamMember.full_name,
        tempPassword: result.tempPassword || '',
        role: newTeamMember.role,
        businessName: profile?.insurer_name || profile?.full_name || 'Your Business',
        emailSent: result.emailSent
      })
      setShowTeamSuccessModal(true)

    } catch (error) {
      setTeamErrorModalData({
        title: 'Error Occurred',
        message: `An unexpected error occurred: ${(error as any).message}`
      })
      setShowTeamErrorModal(true)
    } finally {
      setAddingTeamMember(false)
    }
  }

  const removeTeamMember = async (userId: string) => {
    if (!profile) return

    setConfirmModalData({
      title: 'Remove Team Member',
      message: 'Are you sure you want to remove this team member? This action cannot be undone.',
      onConfirm: async () => {
        const { error } = await supabase
          .from('business_users')
          .delete()
          .eq('business_id', profile.id)
          .eq('user_id', userId)

        if (error) {
          setTeamErrorModalData({
            title: 'Removal Failed',
            message: `Error removing team member: ${error.message}`
          })
          setShowTeamErrorModal(true)
          return
        }

        await loadTeamMembers()
      }
    })
    setShowConfirmModal(true)
  }

  // Close user menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (showUserMenu && !(event.target as HTMLElement).closest('.user-menu-container')) {
        setShowUserMenu(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [showUserMenu])

  const pendingClaims = claims.filter(c => c.status === 'pending')
  const approvedClaims = claims.filter(c => c.status === 'approved')
  const totalPayout = claims.filter(c => c.status === 'paid').reduce((sum, c) => sum + Number(c.insurer_payout), 0)

  return (
    <div className="dashboard-layout">
      <aside className={`sidebar ${mobileMenuOpen ? 'open' : ''}`}>
        <div className="sidebar-brand">
          <span>Mk</span>ononi
          <button
            className="mobile-close-btn"
            onClick={() => setMobileMenuOpen(false)}
            style={{
              position: 'absolute',
              right: '1rem',
              top: '1.5rem',
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              color: 'var(--text)',
              padding: '0.5rem'
            }}
          >
            <X size={20} />
          </button>
        </div>
        <nav className="sidebar-nav">
          <div className="sidebar-section">
            <div className="sidebar-section-title">Insurance</div>
            <div className={`sidebar-item ${tab === 'overview' ? 'active' : ''}`} onClick={() => setTab('overview')}>
              <Shield size={20} />
              <span>Dashboard</span>
            </div>
            <div className={`sidebar-item ${tab === 'claims' ? 'active' : ''}`} onClick={() => setTab('claims')}>
              <FileText size={20} />
              <span>Claims</span>
              {pendingClaims.length > 0 && (
                <span style={{
                  marginLeft: 'auto',
                  background: 'var(--red)',
                  color: 'white',
                  padding: '0.125rem 0.5rem',
                  borderRadius: '9999px',
                  fontSize: '0.75rem',
                  fontWeight: 700,
                }}>
                  {pendingClaims.length}
                </span>
              )}
            </div>
            <div className={`sidebar-item ${tab === 'policies' ? 'active' : ''}`} onClick={() => setTab('policies')}>
              <CheckCircle size={20} />
              <span>Policies</span>
            </div>
          </div>
          <div className="sidebar-section">
            <div className="sidebar-section-title">Reports</div>
            <div className={`sidebar-item ${tab === 'reports' ? 'active' : ''}`} onClick={() => setTab('reports')}>
              <BarChart size={20} />
              <span>Reports</span>
            </div>
          </div>
          <div className="sidebar-section">
            <div className="sidebar-section-title">Account</div>
            <div className={`sidebar-item ${tab === 'team' ? 'active' : ''}`} onClick={() => setTab('team')}>
              <Users size={20} />
              <span>Team</span>
            </div>
            <div className={`sidebar-item ${tab === 'settings' ? 'active' : ''}`} onClick={() => setTab('settings')}>
              <Settings size={20} />
              <span>Settings</span>
            </div>
          </div>
        </nav>
        <div className="sidebar-bottom-fixed">
          <div className="sidebar-footer">
            <div className="name">{profile?.insurer_name || 'Insurer'}</div>
            <div className="role">{profile?.role || 'insurer'}</div>
            <button className="sign-out" onClick={signOut}>
              <LogOut size={18} />
              <span>Sign Out</span>
            </button>
          </div>
        </div>
      </aside>

      {/* Mobile Overlay */}
      {mobileMenuOpen && (
        <div
          onClick={() => setMobileMenuOpen(false)}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.5)',
            zIndex: 99
          }}
          className="mobile-overlay"
        />
      )}

      <main className="dashboard-main">
        <header className="dashboard-header">
          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
            <button
              className="mobile-menu-btn"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              style={{
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                padding: '0.5rem',
                color: 'var(--text)'
              }}
            >
              <Menu size={24} />
            </button>
            <div>
              <h1>
                {tab === 'overview' && 'Insurance Dashboard'}
                {tab === 'claims' && 'Claims Management'}
                {tab === 'policies' && 'Policies'}
                {tab === 'reports' && 'Reports'}
                {tab === 'team' && 'Team Management'}
                {tab === 'settings' && 'Settings'}
              </h1>
              <p className="subtitle">
                Welcome back, {profile?.insurer_name?.split(' ')[0] || 'Insurer'}
              </p>
            </div>
          </div>
          <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
            <div className="user-menu-container" style={{ position: 'relative' }}>
              <button
                onClick={() => setShowUserMenu(!showUserMenu)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  padding: '0.5rem 0.75rem',
                  background: 'white',
                  border: '1px solid var(--border)',
                  borderRadius: 'var(--radius-sm)',
                  cursor: 'pointer',
                  fontWeight: 600,
                  color: 'var(--text)',
                  fontSize: '0.8rem'
                }}
                onMouseEnter={(e) => e.currentTarget.style.background = 'var(--bg)'}
                onMouseLeave={(e) => e.currentTarget.style.background = 'white'}
              >
                <User size={16} />
                <span>{profile?.insurer_name?.split(' ')[0] || 'Insurer'}</span>
                <ChevronDown size={14} />
              </button>
              {showUserMenu && (
                <div
                  style={{
                    position: 'absolute',
                    top: '100%',
                    right: 0,
                    marginTop: '0.5rem',
                    background: 'white',
                    border: '1px solid var(--border)',
                    borderRadius: 'var(--radius-sm)',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                    minWidth: '160px',
                    zIndex: 1000
                  }}
                >
                  <button
                    onClick={() => { setTab('settings'); setShowUserMenu(false) }}
                    style={{
                      width: '100%',
                      padding: '0.75rem 1rem',
                      border: 'none',
                      background: 'none',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem',
                      fontSize: '0.875rem',
                      color: 'var(--text)',
                      fontWeight: 500
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.background = 'var(--bg)'}
                    onMouseLeave={(e) => e.currentTarget.style.background = 'white'}
                  >
                    <User size={16} />
                    Profile
                  </button>
                  <button
                    onClick={() => { signOut(); setShowUserMenu(false) }}
                    style={{
                      width: '100%',
                      padding: '0.75rem 1rem',
                      border: 'none',
                      background: 'none',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem',
                      fontSize: '0.875rem',
                      color: '#dc2626',
                      fontWeight: 500
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.background = 'var(--bg)'}
                    onMouseLeave={(e) => e.currentTarget.style.background = 'white'}
                  >
                    <LogOut size={16} />
                    Sign Out
                  </button>
                </div>
              )}
            </div>
            <button className="btn btn-outline">
              <Bell size={18} />
              <span>Notifications</span>
            </button>
          </div>
        </header>

        <div className="dashboard-content">
          {tab === 'overview' && (
            <>
              <div className="stats-grid">
                <div className="stat-card">
                  <div className="stat-icon" style={{ background: 'var(--red-light)', color: 'var(--red)' }}>
                    <AlertTriangle size={24} />
                  </div>
                  <div className="stat-label">Pending Claims</div>
                  <div className="stat-value">{pendingClaims.length}</div>
                  <div className="stat-trend">
                    <Clock size={14} />
                    Awaiting review
                  </div>
                </div>
                <div className="stat-card">
                  <div className="stat-icon" style={{ background: 'var(--green-light)', color: 'var(--green)' }}>
                    <CheckCircle size={24} />
                  </div>
                  <div className="stat-label">Approved Claims</div>
                  <div className="stat-value">{approvedClaims.length}</div>
                  <div className="stat-trend positive">
                    <TrendingUp size={14} />
                    Ready for payment
                  </div>
                </div>
                <div className="stat-card">
                  <div className="stat-icon" style={{ background: 'var(--purple-light)', color: 'var(--purple)' }}>
                    <DollarSign size={24} />
                  </div>
                  <div className="stat-label">Total Payouts</div>
                  <div className="stat-value">KES {totalPayout.toLocaleString()}</div>
                  <div className="stat-trend">
                    <BarChart3 size={14} />
                    Claims paid
                  </div>
                </div>
                <div className="stat-card">
                  <div className="stat-icon" style={{ background: 'var(--blue-light)', color: 'var(--blue)' }}>
                    <Activity size={24} />
                  </div>
                  <div className="stat-label">Total Claims</div>
                  <div className="stat-value">{claims.length}</div>
                  <div className="stat-trend">
                    <FileText size={14} />
                    All claims filed
                  </div>
                </div>
              </div>

              <div className="card">
                <h2 className="card-title">Recent Claims</h2>
                {claims.length === 0 ? (
                  <div style={{ textAlign: 'center', padding: '3rem', color: 'var(--muted)' }}>
                    <FileText size={48} style={{ margin: '0 auto 1rem', opacity: 0.5 }} />
                    <p>No claims filed yet</p>
                  </div>
                ) : (
                  <div className="data-table-wrapper">
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th>Reference</th>
                        <th>Policy Holder</th>
                        <th>Amount</th>
                        <th>Status</th>
                        <th>Date</th>
                        <th>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {claims.slice(0, 10).map(claim => (
                        <tr key={claim.id}>
                          <td style={{ fontWeight: 600 }}>{claim.reference}</td>
                          <td>{claim.policy_holder_name}</td>
                          <td style={{ fontWeight: 600 }}>
                            KES {Number(claim.total_amount).toLocaleString()}
                          </td>
                          <td>
                            <span className={`badge ${
                              claim.status === 'paid' ? 'badge-green' :
                              claim.status === 'approved' ? 'badge-blue' :
                              claim.status === 'flagged' ? 'badge-red' :
                              claim.status === 'rejected' ? 'badge-gray' :
                              claim.status === 'disputed' ? 'badge-red' :
                              'badge-amber'
                            }`}>
                              {claim.status}
                            </span>
                          </td>
                          <td>{new Date(claim.created_at).toLocaleDateString()}</td>
                          <td>
                            <button className="btn btn-primary btn-sm">View</button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  </div>
                )}
              </div>
            </>
          )}

          {tab === 'claims' && (
            <div className="card">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
                <h2 className="card-title" style={{ fontSize: '1.25rem', color: 'var(--text)' }}>Claims Management</h2>
                <button className="btn btn-primary">
                  <Plus size={18} />
                  New Claim
                </button>
              </div>
              {claims.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '3rem', color: 'var(--muted)' }}>
                  <FileText size={48} style={{ margin: '0 auto 1rem', opacity: 0.5 }} />
                  <p>No claims filed</p>
                </div>
              ) : (
                <div className="data-table-wrapper">
                  <table className="data-table">
                  <thead>
                    <tr>
                      <th>Reference</th>
                      <th>Policy Holder</th>
                      <th>Policy Number</th>
                      <th>Amount</th>
                      <th>Status</th>
                      <th>Date</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {claims.map(claim => (
                      <tr key={claim.id}>
                        <td style={{ fontWeight: 600 }}>{claim.reference}</td>
                        <td>{claim.policy_holder_name}</td>
                        <td>{claim.policy_number}</td>
                        <td style={{ fontWeight: 600 }}>
                          KES {Number(claim.total_amount).toLocaleString()}
                        </td>
                        <td>
                          <span className={`badge ${
                            claim.status === 'paid' ? 'badge-green' :
                            claim.status === 'approved' ? 'badge-blue' :
                            claim.status === 'flagged' ? 'badge-red' :
                            claim.status === 'rejected' ? 'badge-gray' :
                            claim.status === 'disputed' ? 'badge-red' :
                            'badge-amber'
                          }`}>
                            {claim.status}
                          </span>
                        </td>
                        <td>{new Date(claim.created_at).toLocaleDateString()}</td>
                        <td>
                          <button className="btn btn-outline btn-sm">View</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                  </div>
              )}
            </div>
          )}

          {tab === 'policies' && (
            <div className="card">
              <h2 className="card-title" style={{ fontSize: '1.25rem', color: 'var(--text)', marginBottom: '1.5rem' }}>Active Policies</h2>
              <div style={{ textAlign: 'center', padding: '3rem', color: 'var(--muted)' }}>
                <CheckCircle size={48} style={{ margin: '0 auto 1rem', opacity: 0.5 }} />
                <p>No policies to display</p>
              </div>
            </div>
          )}

          {tab === 'reports' && (
            <div className="card">
              <h2 className="card-title" style={{ fontSize: '1.25rem', color: 'var(--text)', marginBottom: '1.5rem' }}>Insurance Reports</h2>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '1.5rem', marginBottom: '2rem' }}>
                <div style={{ padding: '1.5rem', background: 'var(--bg)', borderRadius: 'var(--radius)', border: '2px solid var(--border)' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1rem' }}>
                    <FileText size={24} style={{ color: 'var(--blue)' }} />
                    <div>
                      <div style={{ fontSize: '0.875rem', color: 'var(--muted)', fontWeight: 600 }}>Total Claims</div>
                      <div style={{ fontSize: '1.5rem', fontWeight: 700, color: 'var(--text)' }}>{claims.length}</div>
                    </div>
                  </div>
                </div>
                <div style={{ padding: '1.5rem', background: 'var(--bg)', borderRadius: 'var(--radius)', border: '2px solid var(--border)' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1rem' }}>
                    <CheckCircle size={24} style={{ color: 'var(--green)' }} />
                    <div>
                      <div style={{ fontSize: '0.875rem', color: 'var(--muted)', fontWeight: 600 }}>Approved</div>
                      <div style={{ fontSize: '1.5rem', fontWeight: 700, color: 'var(--text)' }}>{claims.filter(c => c.status === 'approved').length}</div>
                    </div>
                  </div>
                </div>
                <div style={{ padding: '1.5rem', background: 'var(--bg)', borderRadius: 'var(--radius)', border: '2px solid var(--border)' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1rem' }}>
                    <DollarSign size={24} style={{ color: 'var(--red)' }} />
                    <div>
                      <div style={{ fontSize: '0.875rem', color: 'var(--muted)', fontWeight: 600 }}>Total Payouts</div>
                      <div style={{ fontSize: '1.5rem', fontWeight: 700, color: 'var(--text)' }}>KES 0</div>
                    </div>
                  </div>
                </div>
              </div>
              <div style={{ textAlign: 'center', padding: '3rem', color: 'var(--muted)' }}>
                <BarChart3 size={48} style={{ margin: '0 auto 1rem', opacity: 0.5 }} />
                <div style={{ fontSize: '1rem', fontWeight: 500 }}>More reports coming soon</div>
                <div style={{ fontSize: '0.875rem', marginTop: '0.5rem' }}>Detailed analytics and charts will be available here</div>
              </div>
            </div>
          )}

          {tab === 'team' && (
            <div className="card" style={{ marginBottom: '2rem' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                  <div style={{
                    width: '48px',
                    height: '48px',
                    borderRadius: '12px',
                    background: 'linear-gradient(135deg, #dc2626 0%, #b91c1c 100%)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    <Users size={24} color="white" />
                  </div>
                  <div>
                    <h2 className="card-title" style={{ fontSize: '1.5rem', color: 'var(--text)', marginBottom: '0.25rem' }}>Team Members</h2>
                    <p style={{ color: 'var(--text-secondary)', fontSize: '0.875rem', margin: 0 }}>
                      Manage staff members who can access your dashboard
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => setShowAddTeamMemberModal(true)}
                  style={{
                    padding: '0.75rem 1.5rem',
                    background: 'linear-gradient(135deg, #dc2626 0%, #b91c1c 100%)',
                    border: 'none',
                    borderRadius: '8px',
                    color: 'white',
                    fontSize: '0.875rem',
                    fontWeight: 600,
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                    transition: 'all 0.2s',
                    boxShadow: '0 2px 4px rgba(220, 38, 38, 0.2)'
                  }}
                  onMouseOver={(e) => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 4px 6px rgba(220, 38, 38, 0.3)' }}
                  onMouseOut={(e) => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 2px 4px rgba(220, 38, 38, 0.2)' }}
                >
                  <Plus size={18} />
                  Add Team Member
                </button>
              </div>

              {teamMembers.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '3rem', color: 'var(--text-secondary)' }}>
                  <Users size={48} style={{ margin: '0 auto 1rem', opacity: 0.5 }} />
                  <p>No team members yet</p>
                  <p style={{ fontSize: '0.875rem' }}>Add staff members to help manage your operations</p>
                </div>
              ) : (
                <div style={{ display: 'grid', gap: '1rem', marginTop: '1.5rem' }}>
                  {teamMembers.map((member) => (
                    <div
                      key={member.id}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        padding: '1rem',
                        background: 'white',
                        borderRadius: '12px',
                        border: '1px solid var(--border)',
                        boxShadow: '0 1px 3px rgba(0,0,0,0.08)'
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                        <div style={{
                          width: '40px',
                          height: '40px',
                          borderRadius: '50%',
                          background: 'var(--bg)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: '1.25rem',
                          fontWeight: 700,
                          color: 'var(--text-secondary)'
                        }}>
                          {member.profiles?.full_name?.charAt(0).toUpperCase() || 'U'}
                        </div>
                        <div>
                          <div style={{ fontWeight: 600, color: 'var(--text)' }}>
                            {member.profiles?.full_name || 'Unknown'}
                          </div>
                          <div style={{ fontSize: '0.875rem', color: 'var(--text-secondary)' }}>
                            {member.profiles?.email || 'No email'}
                          </div>
                        </div>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <span style={{
                          padding: '0.25rem 0.75rem',
                          borderRadius: '20px',
                          fontSize: '0.75rem',
                          fontWeight: 600,
                          background: member.role === 'admin' ? '#fee2e2' : 'var(--bg)',
                          color: member.role === 'admin' ? '#dc2626' : 'var(--text-secondary)'
                        }}>
                          {member.role === 'admin' ? 'Admin' : 'Staff'}
                        </span>
                        <button
                          onClick={() => removeTeamMember(member.user_id)}
                          style={{
                            padding: '0.5rem 0.75rem',
                            borderRadius: '8px',
                            background: 'linear-gradient(135deg, #dc2626 0%, #b91c1c 100%)',
                            border: 'none',
                            color: 'white',
                            fontSize: '0.875rem',
                            fontWeight: 600,
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '0.5rem',
                            transition: 'all 0.2s',
                            boxShadow: '0 2px 4px rgba(220, 38, 38, 0.2)'
                          }}
                          onMouseOver={(e) => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 4px 6px rgba(220, 38, 38, 0.3)' }}
                          onMouseOut={(e) => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 2px 4px rgba(220, 38, 38, 0.2)' }}
                        >
                          <Trash2 size={14} />
                          Remove
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {tab === 'settings' && (
            <div className="card">
              <h2 className="card-title" style={{ fontSize: '1.25rem', color: 'var(--text)', marginBottom: '1.5rem' }}>Account Settings</h2>
              <div style={{ maxWidth: '600px' }}>
                <div style={{ marginBottom: '2rem' }}>
                  <label style={{ display: 'block', fontWeight: 600, marginBottom: '0.5rem' }}>Company Name</label>
                  <input
                    type="text"
                    className="form-input"
                    defaultValue={profile?.insurer_name}
                    style={{ width: '100%', padding: '0.75rem 1rem', border: '2px solid var(--border)', borderRadius: 'var(--radius-sm)' }}
                  />
                </div>
                <div style={{ marginBottom: '2rem' }}>
                  <label style={{ display: 'block', fontWeight: 600, marginBottom: '0.5rem' }}>Contact Phone</label>
                  <input
                    type="tel"
                    className="form-input"
                    defaultValue={profile?.phone}
                    style={{ width: '100%', padding: '0.75rem 1rem', border: '2px solid var(--border)', borderRadius: 'var(--radius-sm)' }}
                  />
                </div>
                <button className="btn btn-primary">Save Changes</button>
              </div>
            </div>
          )}

          {/* Add Team Member Modal */}
          {showAddTeamMemberModal && (
            <div style={{
              position: 'fixed',
              inset: 0,
              background: 'rgba(0,0,0,0.5)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 1000
            }}>
              <div style={{
                background: 'white',
                borderRadius: '16px',
                padding: '2rem',
                maxWidth: '500px',
                width: '90%',
                maxHeight: '90vh',
                overflowY: 'auto'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1.5rem' }}>
                  <h2 style={{ fontSize: '1.25rem', fontWeight: 700, color: 'var(--text)', margin: 0 }}>Add Team Member</h2>
                  <button
                    onClick={() => setShowAddTeamMemberModal(false)}
                    style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '0.5rem' }}
                  >
                    <X size={24} style={{ color: 'var(--text-secondary)' }} />
                  </button>
                </div>

                <div style={{ display: 'grid', gap: '1rem' }}>
                  <div>
                    <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 600, color: 'var(--text)', marginBottom: '0.5rem' }}>
                      Full Name *
                    </label>
                    <input
                      type="text"
                      value={newTeamMember.full_name}
                      onChange={(e) => setNewTeamMember({ ...newTeamMember, full_name: e.target.value })}
                      placeholder="John Doe"
                      style={{
                        width: '100%',
                        padding: '0.875rem',
                        border: '2px solid var(--border)',
                        borderRadius: '0.75rem',
                        fontSize: '1rem'
                      }}
                    />
                  </div>

                  <div>
                    <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 600, color: 'var(--text)', marginBottom: '0.5rem' }}>
                      Email *
                    </label>
                    <input
                      type="email"
                      value={newTeamMember.email}
                      onChange={(e) => setNewTeamMember({ ...newTeamMember, email: e.target.value })}
                      placeholder="john@example.com"
                      style={{
                        width: '100%',
                        padding: '0.875rem',
                        border: '2px solid var(--border)',
                        borderRadius: '0.75rem',
                        fontSize: '1rem'
                      }}
                    />
                  </div>

                  <div>
                    <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 600, color: 'var(--text)', marginBottom: '0.5rem' }}>
                      Role *
                    </label>
                    <select
                      value={newTeamMember.role}
                      onChange={(e) => setNewTeamMember({ ...newTeamMember, role: e.target.value as 'admin' | 'staff' })}
                      style={{
                        width: '100%',
                        padding: '0.875rem',
                        border: '2px solid var(--border)',
                        borderRadius: '0.75rem',
                        fontSize: '1rem',
                        background: 'white'
                      }}
                    >
                      <option value="staff">Staff - Full access to dashboard</option>
                      <option value="admin">Admin - Full access + can manage team</option>
                    </select>
                  </div>

                  <div style={{
                    padding: '1rem',
                    background: 'var(--bg)',
                    borderRadius: '8px',
                    fontSize: '0.875rem',
                    color: 'var(--text-secondary)'
                  }}>
                    <strong>Note:</strong> The team member will be created with a strong temporary password (12+ characters with uppercase, lowercase, numbers, and special characters). They should change it after first login.
                  </div>

                  <div style={{ display: 'flex', gap: '0.75rem', marginTop: '2rem' }}>
                    <button
                      onClick={() => {
                        setShowAddTeamMemberModal(false)
                        setNewTeamMember({ email: '', full_name: '', role: 'staff' })
                      }}
                      style={{
                        flex: 1,
                        padding: '0.75rem 1rem',
                        background: 'white',
                        border: '2px solid var(--border)',
                        borderRadius: 'var(--radius-sm)',
                        color: 'var(--text)',
                        fontSize: '0.875rem',
                        fontWeight: 600,
                        cursor: 'pointer'
                      }}
                    >
                      Cancel
                    </button>
                    <button
                      onClick={addTeamMember}
                      disabled={addingTeamMember || !newTeamMember.email || !newTeamMember.full_name}
                      style={{
                        flex: 1,
                        padding: '0.75rem 1rem',
                        background: 'linear-gradient(135deg, #dc2626 0%, #b91c1c 100%)',
                        border: 'none',
                        borderRadius: 'var(--radius-sm)',
                        color: 'white',
                        fontSize: '0.875rem',
                        fontWeight: 600,
                        cursor: addingTeamMember ? 'not-allowed' : 'pointer',
                        opacity: addingTeamMember ? 0.6 : 1
                      }}
                    >
                      {addingTeamMember ? 'Adding...' : 'Add Team Member'}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </main>

      {/* Team Success Modal */}
      <TeamMemberSuccessModal
        isOpen={showTeamSuccessModal}
        onClose={() => setShowTeamSuccessModal(false)}
        memberData={teamSuccessModalData}
        emailSent={teamSuccessModalData.emailSent}
      />

      {/* Team Error Modal */}
      <ErrorModal
        isOpen={showTeamErrorModal}
        onClose={() => setShowTeamErrorModal(false)}
        title={teamErrorModalData.title}
        message={teamErrorModalData.message}
      />

      {/* Confirm Modal */}
      <ConfirmModal
        isOpen={showConfirmModal}
        onClose={() => setShowConfirmModal(false)}
        onConfirm={confirmModalData.onConfirm}
        title={confirmModalData.title}
        message={confirmModalData.message}
        confirmText="Remove"
        cancelText="Cancel"
      />
    </div>
  )
}
